<template>
  <div class="brief-wrapper">
    <div class="mb-4">
      <v-chip small color="primary" outlined class="mr-2">
        <v-icon left small>mdi-domain</v-icon>
        {{ domain }}
      </v-chip>
      <v-chip v-if="initRouting" small color="secondary" outlined class="mr-2">
        <v-icon left x-small>mdi-tag-outline</v-icon>
        Devis quantitatif
      </v-chip>
      <v-chip v-if="status === 'generating' && hasContent" small color="warning" outlined>
        <v-icon left small>mdi-loading mdi-spin</v-icon>
        Génération en cours...
      </v-chip>
    </div>

    <!-- Loader: shown while generating before any content arrives -->
    <div v-if="status === 'generating' && !hasContent" class="brief-loader d-flex flex-column align-center justify-center">
      <div class="logo-pulse">
        <v-img :src="$theme.assets.logoContent" width="120" contain class="mb-3" />
      </div>
      <div class="thinking mb-3">
        <span class="dot secondary"></span>
        <span class="dot secondary"></span>
        <span class="dot secondary"></span>
      </div>
      <p class="mb-0 text-body-2 grey--text fade-step" :style="{ animationDelay: stepDelays[0] }">Analyse du contexte projet...</p>
      <p class="mb-0 text-body-2 grey--text fade-step" :style="{ animationDelay: stepDelays[1] }">Consultation du catalogue métier...</p>
      <p class="mb-0 text-body-2 grey--text fade-step" :style="{ animationDelay: stepDelays[2] }">Structuration du brief...</p>
      <p class="mb-0 text-body-2 grey--text fade-step" :style="{ animationDelay: stepDelays[3] }">Génération du tableau quantitatif...</p>
      <p class="mb-0 text-body-2 grey--text fade-step" :style="{ animationDelay: stepDelays[4] }">Finalisation...</p>
    </div>

    <!-- Progressive brief: fills in as SSE chunks arrive, then shows complete editable view -->
    <div v-if="effectiveBrief">
      <BriefFeedbackPanel
        v-if="status === 'complete'"
        :project-id="projectId"
        :session-id="sessionId"
      />

      <!-- Title — full width -->
      <h2
        v-if="effectiveBrief.title || typingTitle"
        class="text-h6 font-weight-bold mb-4"
        :class="{ 'typing-cursor': !!typingTitle }"
      >{{ effectiveBrief.title || typingTitle }}</h2>

      <!-- Description 50% | Hypotheses 50% -->
      <v-row v-if="effectiveBrief.description || typingDescription || (effectiveBrief.assumptions && effectiveBrief.assumptions.length)" class="mb-2" no-gutters>
        <v-col v-if="effectiveBrief.description || typingDescription" cols="12" md="6" class="pr-md-4">
          <div class="text-caption text-uppercase grey--text font-weight-medium mb-2">
            <v-icon small class="mb-1">mdi-sign-text</v-icon>
            Description du projet
          </div>
          <p class="text-body-1" :class="{ 'typing-cursor': !!typingDescription }">{{ effectiveBrief.description || typingDescription }}</p>
        </v-col>
        <v-col v-if="effectiveBrief.assumptions && effectiveBrief.assumptions.length" cols="12" md="6">
          <div class="text-caption text-uppercase grey--text font-weight-medium mb-2">
            <v-icon small class="mb-1">mdi-cube-outline</v-icon>
            Hypothèses retenues
          </div>
          <v-list dense class="pa-0 pl-2">
            <v-list-item v-for="(a, i) in visibleAssumptions" :key="i" class="px-0">
              <v-list-item-icon class="mr-2 my-auto">
                <v-icon small>mdi-information-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2 font-weight-medium">{{ a.title }}</v-list-item-title>
                <v-list-item-subtitle class="text-caption">{{ a.short_description || a.description }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <a
            v-if="effectiveBrief.assumptions.length > 3"
            class="text-caption primary--text"
            style="cursor:pointer"
            @click="assumptionsExpanded = !assumptionsExpanded"
          >{{ assumptionsExpanded ? 'Réduire' : `Voir tout (${effectiveBrief.assumptions.length})` }}</a>
        </v-col>
      </v-row>

      <!-- Material — full width -->
      <MaterialTable
        v-if="effectiveBrief.material && effectiveBrief.material.length"
        :material="effectiveBrief.material"
        :options="effectiveBrief.options || []"
        :status="status"
        :project-id="projectId"
      />
    </div>
  </div>
</template>

<script>
import BriefFeedbackPanel from './BriefFeedbackPanel.vue'
import MaterialTable from './MaterialTable.vue'

function extractItems(raw, key) {
  const idx = raw.indexOf(`"${key}"`)
  if (idx === -1) return []
  let i = idx + key.length + 2
  while (i < raw.length && /[\s:]/.test(raw[i])) i++
  if (raw[i] !== '[') return []
  i++
  const items = []
  while (i < raw.length) {
    while (i < raw.length && /[\s,]/.test(raw[i])) i++
    if (i >= raw.length || raw[i] === ']') break
    if (raw[i] !== '{') break
    let depth = 0, inStr = false, esc = false, j = i
    while (j < raw.length) {
      const c = raw[j]
      if (esc) { esc = false; j++; continue }
      if (c === '\\' && inStr) { esc = true; j++; continue }
      if (c === '"') { inStr = !inStr; j++; continue }
      if (!inStr) {
        if (c === '{') depth++
        else if (c === '}') { depth--; if (depth === 0) { j++; break } }
      }
      j++
    }
    if (depth !== 0) break
    try { items.push(JSON.parse(raw.slice(i, j))); i = j } catch { break }
  }
  return items
}

function extractPartialString(raw, key) {
  const markerRe = new RegExp(`"${key}"\\s*:\\s*"`)
  const m = markerRe.exec(raw)
  if (!m) return null
  const start = m.index + m[0].length
  const rest = raw.slice(start)
  let i = 0, esc = false
  while (i < rest.length) {
    if (esc) { esc = false; i++; continue }
    if (rest[i] === '\\') { esc = true; i++; continue }
    if (rest[i] === '"') return null // string complete — effectiveBrief handles it
    i++
  }
  return rest
    .replace(/\\n/g, '\n').replace(/\\t/g, '\t')
    .replace(/\\"/g, '"').replace(/\\\\/g, '\\')
    .replace(/\\$/, '') // strip trailing incomplete escape
}

function parsePartialBrief(raw) {
  if (!raw) return null
  try { return JSON.parse(raw) } catch (_) { /* partial JSON — fall through to field extraction */ }
  const result = {}
  const strField = (key) => {
    const re = new RegExp(`"${key}"\\s*:\\s*"((?:[^"\\\\]|\\\\[\\s\\S])*)"`)
    const m = raw.match(re)
    if (!m) return undefined
    try { return JSON.parse(`"${m[1]}"`) } catch { return undefined }
  }
  const t = strField('title')
  if (t !== undefined) result.title = t
  const d = strField('description')
  if (d !== undefined) result.description = d
  result.assumptions = extractItems(raw, 'assumptions')
  result.material = extractItems(raw, 'material')
  result.options = extractItems(raw, 'options')
  return Object.keys(result).length ? result : null
}

export default {
  name: 'ProjectBrief',
  components: { BriefFeedbackPanel, MaterialTable },
  props: {
    brief: { type: [Object, String], default: null },
    briefRaw: { type: String, default: '' },
    status: { type: String, required: true },
    projectId: { type: [String, Number], default: null },
    domain: { type: String, default: null },
    initRouting: { type: Boolean, default: false },
    sessionId: { type: String, default: null },
  },
  data() {
    return {
      stepDelays: this._generateStepDelays(),
      assumptionsExpanded: false,
    }
  },
  computed: {
    isStaff() {
      return this.$store.getters['settings/isStaff']
    },
    effectiveBrief() {
      if (this.status === 'complete' && this.brief && typeof this.brief === 'object') return this.brief
      return parsePartialBrief(this.briefRaw)
    },
    typingTitle() {
      if (this.status !== 'generating' || this.effectiveBrief?.title) return null
      return extractPartialString(this.briefRaw, 'title')
    },
    typingDescription() {
      if (this.status !== 'generating' || this.effectiveBrief?.description) return null
      return extractPartialString(this.briefRaw, 'description')
    },
    hasContent() {
      return !!(this.effectiveBrief?.title || this.typingTitle)
    },
    visibleAssumptions() {
      const all = this.effectiveBrief?.assumptions || []
      return this.assumptionsExpanded ? all : all.slice(0, 3)
    },
  },
  watch: {
    status(val) {
      if (val === 'generating') {
        this.stepDelays = this._generateStepDelays()
      }
    },
  },
  methods: {
    _generateStepDelays() {
      let acc = 0
      return Array.from({ length: 5 }, (_, i) => {
        if (i === 0) return '0s'
        acc += Math.random() * 10 + 6
        return `${acc.toFixed(2)}s`
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.brief-loader {
  min-height: 50vh;
}

.logo-pulse {
  animation: scale-pulse 2s infinite ease-in-out;
}

@keyframes scale-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}

.thinking {
  display: flex;
  gap: 4px;
}

.dot {
  width: 8px;
  height: 8px;
  background: #bbb;
  border-radius: 50%;
  animation: thinking-blink 1.2s infinite ease-in-out;

  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.4s; }
}

@keyframes thinking-blink {
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
}

.fade-step {
  opacity: 0;
  animation: fade-in 0.6s ease forwards;
}

@keyframes fade-in {
  to { opacity: 1; }
}

.typing-cursor::after {
  content: '▋';
  margin-left: 1px;
  animation: blink-cursor 0.8s step-end infinite;
}
@keyframes blink-cursor {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
.text-caption {
  white-space: normal;
}

</style>
